export type SupportErrorKind =
  | 'rate_limited'
  | 'network'
  | 'timeout'
  | 'permission'
  | 'server'
  | 'client'
  | 'unknown';

export interface SanitizedSupportError {
  status?: number;
  code?: string;
  name?: string;
  kind: SupportErrorKind;
}

type SupportErrorBody = {
  code?: unknown;
};

type SupportErrorShape = {
  name?: unknown;
  status?: unknown;
  statusCode?: unknown;
  code?: unknown;
  response?: {
    status?: unknown;
    data?: SupportErrorBody;
  };
  data?: SupportErrorBody;
};

const SAFE_ERROR_IDENTIFIER_RE = /^[A-Za-z][A-Za-z0-9_.-]{0,63}$/;

const toRecord = (value: unknown): SupportErrorShape => {
  return value && typeof value === 'object' ? (value as SupportErrorShape) : {};
};

const normalizeStatus = (value: unknown): number | undefined => {
  const status = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(status) && status >= 100 && status <= 599 ? status : undefined;
};

const normalizeIdentifier = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  return SAFE_ERROR_IDENTIFIER_RE.test(normalized) ? normalized : undefined;
};

const classifySupportError = (status?: number, code?: string, name?: string): SupportErrorKind => {
  const marker = `${code ?? ''} ${name ?? ''}`.toLowerCase();

  if (status === 429 || marker.includes('rate_limit') || marker.includes('ratelimit')) return 'rate_limited';
  if (marker.includes('timeout') || marker.includes('timedout') || marker.includes('aborted')) return 'timeout';
  if (marker.includes('network') || marker.includes('connection')) return 'network';
  if (status === 401 || status === 403) return 'permission';
  if (status && status >= 500) return 'server';
  if (status && status >= 400) return 'client';

  return 'unknown';
};

export const sanitizeSupportError = (error: unknown): SanitizedSupportError => {
  const maybeError = toRecord(error);
  const status = normalizeStatus(
    maybeError.statusCode ?? maybeError.status ?? maybeError.response?.status
  );
  const code = normalizeIdentifier(
    maybeError.code ?? maybeError.response?.data?.code ?? maybeError.data?.code
  );
  const name = normalizeIdentifier(maybeError.name);

  return {
    ...(status ? { status } : {}),
    ...(code ? { code } : {}),
    ...(name ? { name } : {}),
    kind: classifySupportError(status, code, name)
  };
};
